import type { EventSubtype } from '@consumer/constants/calendar'
import type { BudgetRange } from './conciergeState'
import { INTEREST_TAGS } from './interestTags.data'

// Keyword dictionaries for the programmatic parser. Relationships mirror the real
// canonical list (Consumer::ContactRelationships); occasions and cities are
// curated sets (no canonical source for those yet); interests are the live GiftTag
// corpus the island is served (see setInterestVocabulary), with the Storybook
// fixture (interestTags.data.ts) as the default until then. Keep every key lowercase
// and hyphen-free — the tokenizer normalizes the buyer's text the same way before lookup.

export type PhraseEntry = { canonical: string, words: string[] }

// One interest GiftTag: its canonical name and the match synonyms merchandising gave it.
export type InterestTagEntry = { name: string, variants: string[] }

// Longest phrase across all dictionaries, in words. The tokenizer builds n-grams
// up to this size (e.g. "best friend", "salt lake city").
export const MAX_NGRAM = 3

// Canonical values mirror Consumer::ContactRelationships (app/values/consumer/
// contact_relationships.rb) — the same relationship keys contacts/calendar/
// recommendations use. `words` are curated match synonyms; keep the canonical set
// in sync if that list changes. Non-canonical asks (in-laws, parents) fold into
// the nearest canonical ("family").
const RELATIONSHIPS: PhraseEntry[] = [
  { canonical: 'mom', words: ['mom', 'mommy', 'momma', 'mama', 'mother', 'mum', 'moms', 'mothers'] },
  { canonical: 'dad', words: ['dad', 'daddy', 'father', 'papa', 'pops', 'dads', 'fathers'] },
  { canonical: 'brother', words: ['brother', 'bro', 'brothers'] },
  { canonical: 'sister', words: ['sister', 'sis', 'sisters'] },
  { canonical: 'child', words: ['child', 'children', 'kid', 'kids', 'son', 'sons', 'daughter', 'daughters'] },
  {
    canonical: 'grandparent',
    words: [
      'grandparent', 'grandma', 'grandmother', 'granny', 'nana',
      'grandpa', 'grandfather', 'gramps', 'grandad', 'granddad',
    ],
  },
  { canonical: 'grandchild', words: ['grandchild', 'grandson', 'granddaughter', 'grandkid', 'grandkids'] },
  { canonical: 'aunt', words: ['aunt', 'auntie'] },
  { canonical: 'uncle', words: ['uncle'] },
  { canonical: 'cousin', words: ['cousin', 'cousins'] },
  { canonical: 'partner', words: ['partner', 'fiance', 'fiancee', 'significant other'] },
  { canonical: 'spouse', words: ['spouse', 'wife', 'husband', 'hubby'] },
  { canonical: 'boyfriend', words: ['boyfriend'] },
  { canonical: 'girlfriend', words: ['girlfriend'] },
  { canonical: 'crush', words: ['crush'] },
  { canonical: 'bestie', words: ['bestie', 'besties', 'best friend', 'bff'] },
  { canonical: 'friend', words: ['friend', 'friends', 'buddy', 'pal'] },
  { canonical: 'neighbor', words: ['neighbor', 'neighbour', 'neighbors'] },
  { canonical: 'colleague', words: ['colleague', 'colleagues', 'coworker', 'coworkers', 'workmate'] },
  { canonical: 'boss', words: ['boss', 'manager', 'supervisor'] },
  { canonical: 'direct_report', words: ['direct report'] },
  {
    canonical: 'family',
    words: [
      'family', 'parents', 'relatives',
      'mother in law', 'father in law', 'sister in law', 'brother in law', 'in law',
    ],
  },
]

// Recurring personal dates. `birthday` and `anniversary` are both calendar event
// subtypes AND real occasion GiftTags, so resolving to them lands on vocabulary the
// rest of the product — and the recommendation corpus — actually knows (typed as
// EventSubtype so a rename over there breaks the build here instead of drifting).
//
// friendaversary/workaversary are the deliberate exception: they exist as calendar
// subtypes but NOT as occasion GiftTags — the corpus tags them all `anniversary`
// (db/migrate/20260202014338_migrate_event_gift_tags_to_listings.rb). Occasion is
// an exclusive search filter, so sending a subtype would exclude every
// anniversary-tagged gift. They stay recognized inputs but resolve to `anniversary`.
const EVENT_SUBTYPE_OCCASIONS: Array<{ canonical: EventSubtype, words: string[] }> = [
  { canonical: 'birthday', words: ['birthday', 'birthdays', 'bday', 'b day'] },
  {
    canonical: 'anniversary',
    words: [
      'anniversary', 'anniversaries', 'wedding anniversary',
      'friendaversary', 'friendiversary', 'friend anniversary', 'friendsversary',
      'workaversary', 'workiversary', 'work anniversary',
    ],
  },
]

// The rest are curated: there's no canonical source for one-off occasions (GiftTag
// occasion tags are noisy), so this half is a hand-picked set, not authoritative.
const OCCASIONS: PhraseEntry[] = [
  ...EVENT_SUBTYPE_OCCASIONS,
  { canonical: 'wedding', words: ['wedding', 'weddings'] },
  { canonical: 'graduation', words: ['graduation', 'graduate', 'grad'] },
  { canonical: "valentine's day", words: ["valentine's day", 'valentines day', 'valentine', 'valentines'] },
  { canonical: "mother's day", words: ["mother's day", 'mothers day'] },
  { canonical: "father's day", words: ["father's day", 'fathers day'] },
  { canonical: 'christmas', words: ['christmas', 'xmas'] },
  { canonical: 'hanukkah', words: ['hanukkah', 'chanukah'] },
  { canonical: 'new year', words: ['new year', 'new years', "new year's"] },
  { canonical: 'baby shower', words: ['baby shower'] },
  { canonical: 'new baby', words: ['new baby', 'newborn'] },
  { canonical: 'housewarming', words: ['housewarming', 'new home', 'new house'] },
  { canonical: 'retirement', words: ['retirement', 'retiring'] },
  { canonical: 'promotion', words: ['promotion', 'new job'] },
  { canonical: 'thank you', words: ['thank you', 'thank-you', 'appreciation'] },
  { canonical: 'get well', words: ['get well', 'feel better'] },
  { canonical: 'congratulations', words: ['congratulations', 'congrats'] },
]

// Major, low-ambiguity cities only — deliberately skips names that collide with
// common words (Mobile, Reading, Nice) so free text doesn't misfire.
const CITIES: string[] = [
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia',
  'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Austin', 'Jacksonville',
  'San Francisco', 'Columbus', 'Indianapolis', 'Seattle', 'Denver', 'Boston',
  'Nashville', 'Portland', 'Las Vegas', 'Detroit', 'Memphis', 'Louisville',
  'Milwaukee', 'Albuquerque', 'Tucson', 'Fresno', 'Sacramento', 'Kansas City',
  'Atlanta', 'Miami', 'Raleigh', 'Omaha', 'Minneapolis', 'Tampa', 'New Orleans',
  'Cleveland', 'Honolulu', 'Pittsburgh', 'Cincinnati', 'Orlando', 'Salt Lake City',
  'Charlotte', 'Brooklyn', 'Scottsdale', 'Savannah', 'Asheville', 'Boulder',
  'London', 'Paris', 'Tokyo', 'Toronto', 'Vancouver', 'Montreal', 'Berlin',
  'Barcelona', 'Sydney', 'Melbourne', 'Dublin', 'Amsterdam',
]

// Hyphens and apostrophes collapse to spaces so "mother-in-law", "mother's day"
// and the buyer's possessives ("my dad's") all reduce to the same word tokens.
function normalizeKey (value: string): string {
  return value.toLowerCase().replace(/[-'’]/g, ' ').replace(/\s+/g, ' ').trim()
}

function buildPhraseLookup (entries: PhraseEntry[]): Map<string, string> {
  const lookup = new Map<string, string>()
  for (const { canonical, words } of entries) {
    for (const word of words) lookup.set(normalizeKey(word), canonical)
    lookup.set(normalizeKey(canonical), canonical)
  }
  return lookup
}

export const RELATIONSHIP_LOOKUP = buildPhraseLookup(RELATIONSHIPS)
export const OCCASION_LOOKUP = buildPhraseLookup(OCCASIONS)

export const CITY_LOOKUP: Map<string, string> = new Map(
  CITIES.map(city => [normalizeKey(city), city]),
)

// Every interest name and variant points back to the canonical tag name.
export const INTEREST_LOOKUP: Map<string, string> = new Map()
// Just the canonical names — plural-guessing ("craft" → "crafts") only matches
// here, so it can't reach a variant and mislabel (e.g. "craft beer" as art).
export const INTEREST_NAMES = new Set<string>()

export { normalizeKey }

// Surface spellings to offer as inline completions — deliberately not the lookup
// keys above. Those are normalized for matching, so they've lost their
// punctuation ("valentine s day"), and they resolve to a canonical the buyer
// didn't type: completing "cow" should read "coworker", not "colleague".
export const SUGGESTION_POOLS: Record<'relationship' | 'occasion' | 'location' | 'interests', string[]> = {
  relationship: phraseSurfaces(RELATIONSHIPS),
  occasion: phraseSurfaces(OCCASIONS),
  location: CITIES,
  interests: [],
}

function phraseSurfaces (entries: PhraseEntry[]): string[] {
  return entries.flatMap(({ canonical, words }) => [canonical, ...words])
}

// Rebuilds the interest lookups and completion pool in place, so every importer — the
// parser, chip text, madlibs completions — sees the new corpus without being re-wired.
// The island calls this with the live GiftTag corpus it is served (BuyersConcierge.vue).
export function setInterestVocabulary (entries: InterestTagEntry[]) {
  INTEREST_LOOKUP.clear()
  INTEREST_NAMES.clear()
  for (const { name, variants } of entries) {
    const key = normalizeKey(name)
    INTEREST_LOOKUP.set(key, name)
    INTEREST_NAMES.add(key)
    for (const variant of variants) INTEREST_LOOKUP.set(normalizeKey(variant), name)
  }
  SUGGESTION_POOLS.interests.splice(
    0,
    SUGGESTION_POOLS.interests.length,
    ...entries.flatMap(({ name, variants }) => [name, ...variants]),
  )
}

setInterestVocabulary(INTEREST_TAGS)

const SPELLED_NUMBERS: Record<string, number> = {
  ten: 10,
  fifteen: 15,
  twenty: 20,
  twentyfive: 25,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  hundred: 100,
}

function spelledAmount (text: string): number | null {
  const compact = text.replace(/[\s-]+/g, '')
  if (compact in SPELLED_NUMBERS) return SPELLED_NUMBERS[compact]
  const hundred = text.match(/(one|two|three|four|five)\s+hundred/)
  if (hundred) return { one: 100, two: 200, three: 300, four: 400, five: 500 }[hundred[1]] ?? null
  return null
}

const AMOUNT = String.raw`\$?\s?(\d[\d,]*)\s?(?:dollars|dollar|bucks|usd)?`
const CAP_WORDS = String.raw`under|below|less than|up to|no more than|at most|max(?:imum)?|around|about|~`
const FLOOR_WORDS = String.raw`over|above|more than|at least|min(?:imum)?|starting at`
const RANGE_RE = new RegExp(String.raw`(?:between|from)?\s*${AMOUNT}\s*(?:-|–|to|and)\s*${AMOUNT}`, 'i')
const CAP_RE = new RegExp(String.raw`(?:${CAP_WORDS})\s*${AMOUNT}`, 'i')
const FLOOR_RE = new RegExp(String.raw`(?:${FLOOR_WORDS})\s*${AMOUNT}`, 'i')
const BARE_RE = /\$\s?(\d[\d,]*)|(\d[\d,]*)\s?(?:dollars|dollar|bucks)/i
// A spelled cap needs an explicit currency word, so "under fifty" (people? kids?)
// isn't read as a budget — only "under fifty bucks" is.
const SPELLED_CAP_RE = /(?:under|below|less than|up to|around|about|budget of|budget is)\s+([a-z ]+?)\s+(?:bucks|dollars)\b/i
// A budget must carry a real money marker; bare quantities ("8 and 10", "3 kids")
// are not budgets even when a range/floor word sits nearby.
const MONEY_MARKER = /\$|\b(?:dollars?|bucks|usd)\b/i

function amount (captured: string): number {
  return Number(captured.replace(/,/g, ''))
}

function formatAmount (value: number): string {
  return `$${value}`
}

function budgetLabel (range: BudgetRange): string {
  if (range.min !== null && range.max !== null) return `${formatAmount(range.min)}–${formatAmount(range.max)}`
  if (range.max !== null) return `up to ${formatAmount(range.max)}`
  if (range.min !== null) return `${formatAmount(range.min)}+`
  return ''
}

export type BudgetMatch = { range: BudgetRange, label: string }

// Reads a budget out of free text — a range, a cap, a floor, or a bare amount.
// We never ask for a number; this only fires when the buyer volunteers one.
export function matchBudget (raw: string): BudgetMatch | null {
  const text = raw.toLowerCase()
  if (!MONEY_MARKER.test(text)) return null

  const range = text.match(RANGE_RE)
  if (range) {
    const min = amount(range[1])
    const max = amount(range[2])
    return budgetResult(Math.min(min, max), Math.max(min, max))
  }

  // Cap before floor: "no more than $50" is a cap, but its "more than" substring
  // would match the floor pattern first if the order were reversed.
  const cap = text.match(CAP_RE)
  if (cap) return budgetResult(null, amount(cap[1]))

  const floor = text.match(FLOOR_RE)
  if (floor) return budgetResult(amount(floor[1]), null)

  const spelledCap = text.match(SPELLED_CAP_RE)
  if (spelledCap) {
    const value = spelledAmount(spelledCap[1].trim())
    if (value !== null) return budgetResult(null, value)
  }

  const bare = text.match(BARE_RE)
  if (bare) return budgetResult(null, amount(bare[1] ?? bare[2]))

  return null
}

function budgetResult (min: number | null, max: number | null): BudgetMatch {
  const range: BudgetRange = { min, max }
  return { range, label: budgetLabel(range) }
}

export { budgetLabel }
